import { isValid, setSeconds } from 'date-fns';
import { UseFormReturn } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';
import { Question } from 'src/@types/generalEnquete';

export const useEnqueteAnswerValidator = (methods: UseFormReturn, question: Question, languageCode?: string, questionState?: string) => {
  const { validation } = question;
  const { isRequired, errorMessage } = validation?.required || {};
  const requiredValue = questionState === 'visible' && isRequired;
  const { t } = useTranslation();
  const rulesEnqueteAnswer = {
    required: { value: validation?.required.isRequired, message: validation?.required.errorMessage },
    pattern: {
      value:
        validation?.pattern?.value === 'number'
          ? /^[+,-]?([1-9]\d*|0)(\.\d{1,3})?$/
          : validation?.pattern?.value === 'string'
            ? undefined
            : undefined,
      message: validation?.pattern?.errorMessage,
    },
    max: validation?.max ? { value: validation.max.value, message: validation.max.errorMessage } : undefined,
    min: validation?.min ? { value: validation.min.value, message: validation.min.errorMessage } : undefined,
    minLength: validation?.minLength ? { value: validation.minLength.value, message: validation.minLength.errorMessage } : undefined,
    maxLength: validation?.maxLength ? { value: validation.maxLength.value, message: validation.maxLength.errorMessage } : undefined,
    validate: {
      numberOfDecimalPlaces: (value: string | number | null) => {
        const numberOfDecimalPlaces = validation?.numberOfDecimalPlaces?.value;
        if ((value || `${value}` === '0') && numberOfDecimalPlaces !== undefined) {
          // 小数点以下の桁数チェック。指定された桁数以外は許可しない
          const decimalPartPattern = `\\.\\d{${validation?.numberOfDecimalPlaces?.value}}`;
          const integerPartPattern = `\\-?\\d*`;
          // 指定された桁数が0以外の場合、小数部のパターンをつける
          const pattern = `^${integerPartPattern}${numberOfDecimalPlaces !== 0 ? decimalPartPattern : ''}$`;
          if (!new RegExp(pattern).test(`${value}`)) {
            return validation?.numberOfDecimalPlaces?.errorMessage;
          }
        }
      },
    },
  };

  const rulesEnqueteAnswerCheckbox = {
    validate: {
      numberOfElementsValidate: (values: string[]) => {
        if (validation?.required.isRequired && values.length === 0) {
          return validation.required.errorMessage;
        }

        if (validation?.minNumberOfElements && (values.length === 0 || values.length < validation.minNumberOfElements.value)) {
          return validation.minNumberOfElements.errorMessage;
        }

        if (validation?.maxNumberOfElements && values.length > validation.maxNumberOfElements.value) {
          return validation.maxNumberOfElements.errorMessage;
        }
      },
    },
  };

  const rulesEnqueteAnswerDate = {
    required: { value: requiredValue, message: errorMessage },
    validate: {
      isValidDate: (value: string) => {
        if (!value) return true;
        return isValid(new Date(value)) || `${t('enqueteCreate.validation.errorMessage.dateFormatError', { lng: languageCode })}`;
      },
    },
  };

  //開始日のバリデーションルール
  const rulesEnqueteAnswerStartDate = {
    required: { value: requiredValue, message: errorMessage },
    validate: {
      //日付形式チェック
      isValidDate: (value: Date) => {
        //開始日未入力の場合はチェック不要
        if (!value) return true;
        const date = new Date(value);
        //チェックエラーの場合、エラーを表示する
        return date instanceof Date && !isNaN(date.getTime()) ? true : `${t('enqueteCreate.validation.errorMessage.dateFormatError', { lng: languageCode })}`;
      },
      //開始日、終了日の関連チェック
      isValidPeriod: (value: Date) => {
        //終了日のid取得
        const endFieldId = question.questionItem[1]?.id;
        //終了日の値取得
        const endDateStr = methods.getValues()[endFieldId];
        //終了日のエラーメッセージクリアの関数
        const clearEndFieldError = () => {
          if (methods.getFieldState(endFieldId).error?.type === 'isValidPeriod') {
            methods.clearErrors(endFieldId);
          }
        };
        //開始日未入力の場合のチェック
        if (!value) {
          //必須チェックがtrueの場合、バリデーションを実行する。
          if (requiredValue) {
            //チェックエラーの場合、エラーを表示する
            return errorMessage;
          }
          //終了日入力の場合のチェック
          if (endDateStr) {
            return errorMessage || t('enqueteCreate.validation.errorMessage.validateOnlyEndDateInput', { lng: languageCode });
          }
          clearEndFieldError();
          return true;
        }
        const date = new Date(value);
        const endDateObject = new Date(endDateStr);
        if (isNaN(endDateObject.getTime()) || !endDateStr) return true;
        //開始日、終了日の関連チェック
        if (setSeconds(date, 0).getTime() <= setSeconds(endDateObject, 0).getTime()) {
          clearEndFieldError()
          return true
        } else {
          //開始日が終了日より後の場合、エラーを表示する
          return `${t('enqueteCreate.validation.errorMessage.validateErrorInputStartAndEndDateIsValidPeriod', { lng: languageCode })}`;
        }
      },
    },
  };

  //終了日のバリデーションルール
  const rulesEnqueteAnswerEndDate = {
    required: { value: requiredValue, message: errorMessage },
    validate: {
      //日付形式チェック
      isValidDate: (value: Date) => {
        //終了日未入力の場合はチェック不要
        if (!value) return true;
        const date = new Date(value);
        //チェックエラーの場合、エラーを表示する
        return date instanceof Date && !isNaN(date.getTime()) ? true : `${t('enqueteCreate.validation.errorMessage.dateFormatError', { lng: languageCode })}`;
      },
      //開始日、終了日の関連チェック
      isValidPeriod: (value: Date) => {
        //開始日のid取得
        const startFieldId = question.questionItem[0]?.id;
        //開始日の値取得
        const startDateStr = methods.getValues()[startFieldId];
        //開始日のエラーメッセージクリアの関数
        const clearStartFieldError = () => {
          if (methods.getFieldState(startFieldId).error?.type === 'isValidPeriod') {
            methods.clearErrors(startFieldId);
          }
        };
        //終了日未入力の場合のチェック
        if (!value) {
          //必須チェックがtrueの場合、バリデーションを実行する。
          if (requiredValue) {
            //チェックエラーの場合、エラーを表示する
            return errorMessage;
          }
          //開始日入力の場合のチェック
          if (startDateStr) {
            return errorMessage || t('enqueteCreate.validation.errorMessage.validateOnlyStartDateInput', { lng: languageCode });
          }
          clearStartFieldError();
          return true;
        }
        const date = new Date(value);
        const startDateObject = new Date(startDateStr);
        if (isNaN(startDateObject.getTime()) || !startDateStr) return true;
        //開始日、終了日の関連チェック
        if (setSeconds(date, 0).getTime() >= setSeconds(startDateObject, 0).getTime()) {
          clearStartFieldError();
          return true
        } else {
          //開始日が終了日より後の場合、エラーを表示する
          return `${t('enqueteCreate.validation.errorMessage.validateErrorInputStartAndEndDateIsValidPeriod', { lng: languageCode })}`;
        }
      },
    },
  };


  return {
    rulesEnqueteAnswer,
    rulesEnqueteAnswerCheckbox,
    rulesEnqueteAnswerDate,
    rulesEnqueteAnswerStartDate,
    rulesEnqueteAnswerEndDate
  };
};

